/**
 * Audio Feedback Utility
 * Provides audio cues for user actions - particularly useful for voice input
 * 
 * Uses Web Audio API to generate simple tones without needing external audio files
 */

// Audio context singleton
let audioContext = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

/**
 * Play a simple tone
 * @param {number} frequency - Frequency in Hz
 * @param {number} duration - Duration in milliseconds
 * @param {string} type - Oscillator type: 'sine', 'square', 'triangle', 'sawtooth'
 * @param {number} volume - Volume 0-1
 */
function playTone(frequency, duration = 100, type = 'sine', volume = 0.3) {
  try {
    const ctx = getAudioContext();
    
    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
    
    // Fade out to avoid clicks
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
  } catch (error) {
    console.warn('Audio feedback not available:', error);
  }
}

/**
 * Success sound - ascending two-note chime
 * Played when an item is successfully added
 */
export function playSuccessSound() {
  playTone(523.25, 80, 'sine', 0.2);  // C5
  setTimeout(() => {
    playTone(659.25, 120, 'sine', 0.25);  // E5
  }, 80);
}

/**
 * Error sound - low descending tone
 * Played when an action fails
 */
export function playErrorSound() {
  playTone(293.66, 150, 'triangle', 0.3);  // D4
  setTimeout(() => {
    playTone(220.00, 200, 'triangle', 0.25);  // A3
  }, 100);
}

/**
 * Start listening sound - single rising tone
 * Played when voice input starts
 */
export function playStartListeningSound() {
  playTone(440, 60, 'sine', 0.15);  // A4
  setTimeout(() => {
    playTone(587.33, 80, 'sine', 0.2);  // D5
  }, 50);
}

/**
 * Stop listening sound - single falling tone
 * Played when voice input stops
 */
export function playStopListeningSound() {
  playTone(523.25, 80, 'sine', 0.15);  // C5
  setTimeout(() => {
    playTone(392.00, 100, 'sine', 0.15);  // G4
  }, 60);
}

/**
 * Confirm sound - short blip
 * Played for quick confirmations
 */
export function playConfirmSound() {
  playTone(880, 50, 'sine', 0.15);  // A5
}

/**
 * Check if audio is supported
 */
export function isAudioSupported() {
  return !!(window.AudioContext || window.webkitAudioContext);
}

export default {
  playSuccessSound,
  playErrorSound,
  playStartListeningSound,
  playStopListeningSound,
  playConfirmSound,
  isAudioSupported
};
